import { Injectable } from '@angular/core';
import {
  HttpEvent, HttpInterceptor, HttpHandler, HttpRequest, HttpErrorResponse, HttpResponse
} from '@angular/common/http';
import { Observable, throwError, from, lastValueFrom } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { LoginService } from '@services/login.service';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {

  constructor(private loginService: LoginService) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const authReq = this.secureRequest(req);

    return next.handle(authReq).pipe(
      map((event) => event instanceof HttpResponse
        ? event.clone({ body: this.normalizeApiUrls(event.body) })
        : event),
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401 && error.error?.msg?.includes('expired')) {
          return from(this.handleExpiredToken(authReq, next));
        }
        return throwError(() => error);
      })
    );
  }

  private normalizeApiUrls(value: any): any {
    if (typeof value === 'string') {
      const apiIsSameOrigin = typeof window !== 'undefined'
        && window.location.origin === 'https://clavewinds.shop';
      return apiIsSameOrigin
        ? value.replace(/^https?:\/\/clavewinds\.shop(?=\/api(?:\/|$))/i, '')
        : value;
    }
    if (Array.isArray(value)) return value.map((item) => this.normalizeApiUrls(item));
    if (value && typeof value === 'object'
      && (Object.getPrototypeOf(value) === Object.prototype || Object.getPrototypeOf(value) === null)) {
      return Object.fromEntries(
        Object.entries(value).map(([key, item]) => [key, this.normalizeApiUrls(item)])
      );
    }
    return value;
  }

  private async handleExpiredToken(req: HttpRequest<any>, next: HttpHandler) {
    try {
      await this.loginService.GuestToken();
      return await lastValueFrom(next.handle(this.secureRequest(req)));
    } catch (err) {
      console.error('Error al refrescar token guest', err);
      throw err;
    }
  }

  private secureRequest(req: HttpRequest<any>): HttpRequest<any> {
    let headers = req.headers
      .set('X-Requested-With', 'XMLHttpRequest')
      .set('Accept', 'application/json');

    const csrfToken = this.getCookie('XSRF-TOKEN') || this.getCookie('CSRF-TOKEN');
    if (csrfToken && !req.headers.has('X-XSRF-TOKEN')) {
      headers = headers.set('X-XSRF-TOKEN', decodeURIComponent(csrfToken));
    }

    return req.clone({
      withCredentials: true,
      headers
    });
  }

  private getCookie(name: string): string {
    if (typeof document === 'undefined') return '';
    const match = document.cookie.match(new RegExp('(^|;\\s*)' + name + '=([^;]*)'));
    return match ? match[2] : '';
  }
}
